import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useCustomerAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { extractErr } from "@/lib/api";
import { useStoreBase } from "@/lib/store";
import { useTranslation } from "react-i18next";
import FirebasePhoneBox from "@/components/FirebasePhoneBox";

export default function StorefrontCustomerSignup({ config }) {
  const { t } = useTranslation();
  const { slug, base } = useStoreBase();
  const nav = useNavigate();
  const { signup } = useCustomerAuth();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [phoneToken, setPhoneToken] = useState("");
  const [verifiedPhone, setVerifiedPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const otpOn = Boolean(config?.firebase_otp_active && config?.firebase_otp?.apiKey);

  const submit = async (e) => {
    e.preventDefault();
    if (otpOn && !phoneToken) {
      toast.error("Verify your mobile number before creating the account.");
      return;
    }
    setLoading(true);
    const referral_code = params.get("ref") || (typeof localStorage !== "undefined" ? localStorage.getItem(`ref:${slug}`) : null);
    try {
      await signup({ ...form, referral_code, ...(otpOn ? { firebase_id_token: phoneToken } : {}) });
      toast.success(t("store.accountCreated"));
      nav(`${base}/`);
    }
    catch (e) { toast.error(extractErr(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto w-full px-4 sm:px-6 py-8 sm:py-12">
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-stone-900">{t("store.createAccount")}</h1>
      <p className="text-stone-500 text-sm mt-1">{t("store.createFor", { name: config.store_info?.name })}</p>
      <form onSubmit={submit} className="mt-6 sm:mt-8 space-y-4">
        <div><Label>{t("common.fullName")}</Label><Input required data-testid="csignup-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-11" /></div>
        <div><Label>{t("common.email")}</Label><Input required type="email" data-testid="csignup-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-11" /></div>
        <div><Label>{t("common.password")}</Label><Input required type="password" minLength={6} data-testid="csignup-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 h-11" /></div>
        {otpOn ? (
          <FirebasePhoneBox
            config={config.firebase_otp}
            verifiedPhone={verifiedPhone}
            testPrefix="csignup-otp"
            onVerified={({ token, phone }) => { setPhoneToken(token); setVerifiedPhone(phone || "verified"); }}
          />
        ) : null}
        <Button type="submit" disabled={loading} data-testid="csignup-submit" className="w-full h-11 btn-store-primary rounded-full">{loading ? t("store.creating") : t("store.createAccount")}</Button>
      </form>
      <div className="mt-6 text-sm text-stone-500 text-center">{t("store.haveAccount")} <Link to={`${base}/account/login`} className="text-stone-900 underline" data-testid="csignup-to-login">{t("store.signIn")}</Link></div>
    </div>
  );
}
